/**
 * AntiScraping Middleware Component
 *
 * This component acts as a security layer to prevent automated scraping of the website.
 * It detects and blocks common web scraping tools and automated browsers.
 *
 * Purpose:
 * - Protects website content from being automatically extracted by bots
 * - Prevents unauthorized data collection
 * - Maintains website performance by blocking automated requests
 *
 * How it works:
 * 1. Checks for common scraping indicators in the user's browser
 * 2. Detects automated browser tools and headless browsers
 * 3. Blocks access if suspicious activity is detected
 * 4. Shows an access denied message to automated tools
 *
 */

import React, { useEffect, useState } from "react";

/**
 * AntiScraping Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be protected
 * @returns {React.ReactNode} - Either the protected content or an access denied message
 */
const AntiScraping = ({ children }) => {
  // State to track if the current visitor is a scraper
  const [isScraper, setIsScraper] = useState(false);
  // State to track if the detection process is still running
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    /**
     * Function to detect common web scraping tools and automated browsers
     * Checks multiple indicators to determine if the visitor is a scraper
     */
    const checkForScraper = () => {
      // Get the user's browser information
      const userAgent = navigator.userAgent.toLowerCase();

      // Check for headless browsers (browsers without a visible UI)
      const isHeadless =
        /headless/i.test(userAgent) ||
        /phantomjs/i.test(userAgent) ||
        /puppeteer/i.test(userAgent) ||
        /selenium/i.test(userAgent);

      // Check for automated browser indicators
      // These are properties typically present in automated browsers
      const isAutomated =
        window.navigator.webdriver ||
        window.navigator.__webdriver_evaluate ||
        window.navigator.__webdriver_script_func;

      // Check for common scraping tools in the user agent string
      const isScrapingTool =
        /curl|wget|python-requests|scrapy|beautifulsoup/i.test(userAgent);

      // If any of the checks return true, mark the visitor as a scraper
      if (isHeadless || isAutomated || isScrapingTool) {
        setIsScraper(true);
      }
      setIsChecking(false);
    };

    // Use requestAnimationFrame to ensure DOM is ready before running checks
    requestAnimationFrame(checkForScraper);
  }, []);

  // Show nothing while the detection process is running
  if (isChecking) {
    return null;
  }

  // If a scraper is detected, show the access denied message
  if (isScraper) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div>
          <h1>Access Denied</h1>
          <p>Automated scraping of this website is not allowed.</p>
          <p>Please contact the website administrator for more information.</p>
        </div>
      </div>
    );
  }

  // If no scraper is detected, render the protected content
  return children;
};

export default AntiScraping;
